import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useUserFiles } from '../contexts/UserFilesContext';

interface StatCard {
  label: string;
  value: string;
  color: 'soundwave' | 'neon';
  sublabel?: string;
  to?: string;
}

export const QuickStats: React.FC = () => {
  const { isProUser, isBetaUser } = useAuth();
  const { files, loading: filesLoading } = useUserFiles();

  const stats = useMemo(() => {
    const now = Date.now() / 1000;
    const sevenDaysFromNow = now + 7 * 24 * 60 * 60;
    const threeDaysFromNow = now + 3 * 24 * 60 * 60;

    const expiringCount = files.filter(f => f.ttl && f.ttl <= sevenDaysFromNow).length;

    const soonExpiringTtls = files
      .filter(f => f.ttl && f.ttl > now && f.ttl <= threeDaysFromNow)
      .map(f => f.ttl as number);
    const minExpiryDays =
      soonExpiringTtls.length > 0
        ? Math.max(0, Math.floor((Math.min(...soonExpiringTtls) - now) / (24 * 60 * 60)))
        : undefined;

    return {
      expiringFiles: expiringCount,
      minExpiryDays,
      loading: filesLoading,
    };
  }, [files, filesLoading]);

  const isPro = isProUser() || isBetaUser();

  // Landing surface used to render three counters — Total Files, this middle
  // tile, and Collections. The two flanking counters were pure file-inventory
  // vanity ("N files in the box"), reinforcing the Dropbox reading of the app.
  //
  // The remaining tile only earns its space for FREE users with at least one
  // file inside the 2–7d expiry window — the only place in the app that
  // window is surfaced (ExpiringFilesBanner fires <24h). For Pro/Beta users
  // the tile would read "Recent Uploads: N" directly above the "Recent
  // Uploads" section header on the dashboard grid, a Law of Similarity
  // violation with no functional signal to justify it. For free users with
  // zero expiring files, "Expiring Soon: 0" is decorative empty state. In
  // either case the component collapses instead of rendering a hollow tile.
  if (isPro) return null;
  if (stats.loading || stats.expiringFiles === 0) return null;

  const statCards: StatCard[] = [
    {
      label: 'Expiring Soon',
      value: stats.expiringFiles.toString(),
      color: 'neon',
      sublabel:
        stats.minExpiryDays !== undefined
          ? `first expires in ${stats.minExpiryDays === 0 ? 'less than a day' : `${stats.minExpiryDays} day${stats.minExpiryDays === 1 ? '' : 's'}`}`
          : undefined,
      to: '/my-files?sort=expiring',
    },
  ];

  return (
    <div className="mb-6 max-w-xs">
      {statCards.map((stat, index) => {
        const isOrange = stat.color === 'soundwave';
        const interactive = !!stat.to;

        const tileBody = (
          <>
            <div
              className={`absolute inset-0 opacity-5 bg-gradient-to-br ${
                isOrange
                  ? 'from-soundwave-500 to-soundwave-600'
                  : 'from-neon-500 to-neon-600'
              }`}
            />

            <div className="relative z-10 min-w-0">
              <p
                className={`text-xs font-medium uppercase tracking-wide mb-1 transition-colors ${
                  interactive
                    ? isOrange
                      ? 'group-hover:text-soundwave-400'
                      : 'group-hover:text-neon-400'
                    : ''
                }`}
                style={{ color: 'var(--text-tertiary)' }}
              >
                {stat.label}
              </p>
              <p
                className={`text-2xl md:text-3xl font-bold truncate ${
                  isOrange ? 'text-soundwave-400' : 'text-neon-400'
                }`}
              >
                {stat.value}
              </p>
              {stat.sublabel && (
                <p
                  className="text-xs mt-1 opacity-70"
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  {stat.sublabel}
                </p>
              )}
            </div>
          </>
        );

        const tileClass = `group relative rounded-xl overflow-hidden backdrop-blur-md border p-4 shadow-sm transition-shadow ${
          interactive ? 'hover:shadow-md cursor-pointer' : 'hover:shadow-md'
        }`;
        const tileStyle: React.CSSProperties = {
          backgroundColor: 'var(--background-surface)',
          borderColor: 'var(--border-color)',
        };

        if (interactive && stat.to) {
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="h-full"
            >
              <Link to={stat.to} className={`block h-full ${tileClass}`} style={tileStyle}>
                {tileBody}
              </Link>
            </motion.div>
          );
        }

        return (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.05 }}
            className={tileClass}
            style={tileStyle}
          >
            {tileBody}
          </motion.div>
        );
      })}
    </div>
  );
};
